import React from 'react'
import { observer } from 'mobx-react-lite'

import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'

import {
  Loader,
  MiniWidgetContainer,
  useAccountStore,
  useGlobalCopyStore,
  useGlobalUiStore,
  useScreenSize,
  useWidgetLoadTimer,
} from '../common'
import { MiniWidgetProps } from '../core'

import { getDisplayAccounts } from '../common/utils/AccountFilter'
import { SkeletonLoader } from '../common/components/SkeletonLoader'

import AccountListItem from './components/AccountListItem'

const AccountsMiniWidget: React.FC<MiniWidgetProps> = ({ onPrimaryCtaClick, sx }) => {
  const { isAccountDataLoaded, loadAccountData } = useAccountStore()
  const { accounts: accountsCopy } = useGlobalCopyStore()
  const { isInitialized, selectedAccounts } = useGlobalUiStore()
  const { isDesktop } = useScreenSize()

  useWidgetLoadTimer({
    widgetName: 'AccountsMiniWidget',
    isLoaded: isInitialized,
  })

  React.useEffect(() => {
    if (!isAccountDataLoaded) {
      loadAccountData().finally()
    }
  }, [])

  const displayAccounts = React.useMemo(
    () => getDisplayAccounts(selectedAccounts, !isDesktop ? 3 : 5),
    [selectedAccounts, isDesktop],
  )

  if (!isInitialized) {
    return <Loader />
  }

  return (
    <MiniWidgetContainer
      className="mx-exp-accounts-miniwidget"
      onPrimaryCtaClick={onPrimaryCtaClick}
      primaryCtaLabel={accountsCopy.primary_cta}
      sx={sx}
      title={accountsCopy.mini_title}
    >
      {!isAccountDataLoaded ? (
        <SkeletonLoader sx={{ px: 12, py: 8 }} variant="list" />
      ) : (
        <List>
          {selectedAccounts.length === 0 && (
            <ListItem sx={{ justifyContent: 'center' }}>{accountsCopy.no_accounts}</ListItem>
          )}
          {displayAccounts.map((account, i) => {
            const isLast = displayAccounts.indexOf(account) === displayAccounts.length - 1
            return (
              <React.Fragment key={`account-item${i}`}>
                <AccountListItem
                  account={account}
                  listItemProps={{ sx: { px: 24 } }}
                  shouldShowConnectionStatus={true}
                />
                {!isLast && <Divider aria-hidden="true" variant="inset" />}
              </React.Fragment>
            )
          })}
        </List>
      )}
    </MiniWidgetContainer>
  )
}

export default observer(AccountsMiniWidget)
